import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Avatar, Card } from '../../components/ui';
import { useEventDetail, useEventList } from '../../hooks/queries';
import { MY_COLOR } from '../../lib/colors';
import { useAuth } from '../../stores/auth';
import { RULE_LABEL, eventTiming, formatEventValue } from './event-format';
import { useT } from '../../i18n';

/** Home: my live event (or an invitation / the next public one) with my position. */
export function EventCard() {
  const list = useEventList();
  const me = useAuth((s) => s.user?.id);
  const t = useT();
  const d = list.data;
  const mineLive = d?.mine.find((e) => e.status === 'ACTIVE' && e.joined) ?? d?.mine.find((e) => e.status === 'UPCOMING' && e.joined);
  const current = mineLive ?? d?.invitations[0] ?? d?.live[0] ?? d?.upcoming[0];
  const detail = useEventDetail(current?.status === 'ACTIVE' ? current.id : undefined);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((n) => n + 1), 30_000);
    return () => clearInterval(t);
  }, []);
  if (!current) {
    return d ? (
      <Link to="/events" className="mt-4 flex items-center justify-between rounded-2xl border border-gold/30 bg-gold/10 px-4 py-3 text-sm" data-testid="home-events-link">
        <span className="font-semibold text-gold">{t('🏁 Events — create a race for your crew')}</span>
        <span className="text-gold">›</span>
      </Link>
    ) : null;
  }
  const top = detail.data?.standings.individual.slice(0, 3) ?? [];
  const mine = detail.data?.me?.standing;
  const invited = !mineLive && d?.invitations[0]?.id === current.id;
  const label = t(invited ? "You're invited" : current.joined ? (current.status === 'ACTIVE' ? 'Your event · live' : 'Your event · soon') : current.status === 'ACTIVE' ? 'Event live' : 'Next event');

  return (
    <Link to={`/events/${current.id}`} className="mt-4 block" data-testid="home-event">
      <Card className="border-gold/40 bg-gradient-to-br from-gold/10 to-transparent">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-bold uppercase tracking-widest text-gold">
              {label} · {t(RULE_LABEL[current.rule])}
            </div>
            <div className="truncate font-display text-2xl font-bold">{current.name}</div>
          </div>
          <span className="shrink-0 rounded-full bg-ink-800 px-2.5 py-1 text-xs font-semibold text-ink-200">
            {eventTiming(current.status, current.startsAt, current.endsAt)}
          </span>
        </div>
        {current.status === 'ACTIVE' ? (
          <div className="mt-3 flex items-center justify-between gap-3">
            <div className="flex -space-x-2">
              {top.map((s) => (
                <Avatar key={s.user.id} name={s.user.displayName} color={s.user.id === me ? MY_COLOR : s.user.color} src={s.user.avatarUrl} size="sm" />
              ))}
            </div>
            <div className="text-right text-sm">
              {mine ? (
                <>
                  <span className="font-display text-xl font-bold text-gold">#{mine.rank}</span>
                  <span className="ml-1 text-ink-300">{formatEventValue(current.rule, mine.value)}</span>
                </>
              ) : (
                <span className="text-ink-400">{current.joined ? t('Run to get on the board →') : t('{n} runners · join →', { n: current.participants })}</span>
              )}
            </div>
          </div>
        ) : null}
      </Card>
      <div className="mt-1 text-right text-xs">
        <span className="text-ink-400">{t('All events ›')}</span>
      </div>
    </Link>
  );
}
